import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const location = useLocation();

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    const checkAuth = async () => {
      try {
        console.log('[ProtectedRoute] Starting auth check...');
        
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        console.log('[ProtectedRoute] Session retrieved:', { hasSession: !!session, error: sessionError });
        
        if (sessionError) {
          console.error('[ProtectedRoute] Session error:', sessionError);
          setIsAuthenticated(false);
          setIsAdmin(false);
          return;
        }
        
        if (!session) {
          console.log('[ProtectedRoute] No session found');
          setIsAuthenticated(false);
          setIsAdmin(false);
          return;
        }
        
        console.log('[ProtectedRoute] User authenticated, checking approval and admin role...');
        setIsAuthenticated(true);
        
        // Check approval status
        const { data: approval } = await supabase
          .from('pending_user_approvals')
          .select('status')
          .eq('user_id', session.user.id)
          .maybeSingle();
        
        // If user has pending approval, deny access
        if (approval && approval.status === 'pending') {
          console.log('[ProtectedRoute] User has pending approval');
          setIsAdmin(false);
          return;
        }
        
        // Check if user has admin role with timeout
        const rolesPromise = supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', session.user.id)
          .maybeSingle();
        
        const timeoutPromise = new Promise((_, reject) => {
          timeoutId = setTimeout(() => reject(new Error('Role check timeout')), 5000);
        });
        
        const { data: roles, error: rolesError } = await Promise.race([
          rolesPromise,
          timeoutPromise
        ]) as any;
        
        clearTimeout(timeoutId);
        
        console.log('[ProtectedRoute] Roles query result:', { roles, error: rolesError });
        
        if (rolesError) {
          console.error('[ProtectedRoute] Roles query error:', rolesError);
          setIsAdmin(false);
          return;
        }
        
        console.log('[ProtectedRoute] Admin check complete:', !!roles);
        setIsAdmin(!!roles);
      } catch (error) {
        console.error('[ProtectedRoute] Auth check error:', error);
        setIsAuthenticated(false);
        setIsAdmin(false);
      }
    };

    checkAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('[ProtectedRoute] Auth state changed:', event);
      
      // Only synchronous state updates here
      if (!session) {
        setIsAuthenticated(false);
        setIsAdmin(false);
        return;
      }
      
      setIsAuthenticated(true);
      
      // Defer the database query to avoid blocking the callback
      setTimeout(async () => {
        try {
          // Check approval status
          const { data: approval } = await supabase
            .from('pending_user_approvals')
            .select('status')
            .eq('user_id', session.user.id)
            .maybeSingle();
          
          if (approval && approval.status === 'pending') {
            setIsAdmin(false);
            return;
          }
          
          const { data: roles, error: rolesError } = await supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', session.user.id)
            .maybeSingle();
          
          if (rolesError) {
            console.error('[ProtectedRoute] Roles query error on auth change:', rolesError);
            setIsAdmin(false);
            return;
          }
          
          setIsAdmin(!!roles);
        } catch (error) {
          console.error('[ProtectedRoute] Auth state change error:', error);
          setIsAdmin(false);
        }
      }, 0);
    });

    return () => {
      subscription.unsubscribe();
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  // Loading state
  if (isAuthenticated === null || isAdmin === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Verifying access...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  // Authenticated but not admin - show access denied or pending approval
  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center max-w-md mx-auto p-6">
          <h1 className="text-2xl font-bold mb-4">Access Pending Approval</h1>
          <p className="text-muted-foreground mb-4">
            Your account has been created and is awaiting administrator approval.
          </p>
          <p className="text-sm text-muted-foreground mb-6">
            You'll receive an email notification once your access has been approved. 
            If you have any questions, please contact jason@simnetwireless.com.
          </p>
          <Button onClick={() => supabase.auth.signOut()}>
            Sign Out
          </Button>
        </div>
      </div>
    );
  }

  // Authenticated and admin - render children
  return <>{children}</>;
}
